import {
  FormContext,
  FormInputGroupLayoutContext,
  FormState,
  optimizedRetryOption,
  Select,
  SelectOption,
  SelectProps,
  stateT,
  uniqueGUID,
} from "o4a-react";
import * as React from "react";
import { NormalError } from "savant-connector";
import apiClients, { MultiCloudDatabaseApiVersion } from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { ttlOneMinCaching } from "../../constants/uiConstants";
import { CloudVmClusterSummary, CloudVmClusterSummaryCollection } from "../../gen/clients/mchub-azure-api-client-exa";
import { IdResourceType } from "../../helpers/idHelper";
import { ResourceStatus, responseItemstoArray } from "../../helpers/resourceHelper";
import { useQueryCall } from "../../hooks/useQueryCall";
import { getOciRegion } from "../../utils";

const buildVmClusterLabel = (item: CloudVmClusterSummary): string => {
  if (item.status !== ResourceStatus.Failed && item.lifecycleState !== "FAILED") return item.name;
  return `${item.name} (${stateT(ResourceStatus.Failed)})`;
};

const buildVmClusterOption = (item: CloudVmClusterSummary): SelectOption => ({
  id: item.id,
  text: buildVmClusterLabel(item),
  data: item,
  disabled: item.lifecycleState === "FAILED" || item.status === ResourceStatus.Failed,
});

export interface VMClusterProps extends Omit<SelectProps, "options"> {
  resourceGroupName: string;
  subscriptionId: string;
  location: string;
  onError?: (error: NormalError) => void;
  onMissingDependencies?: (missingDependencies: IdResourceType[]) => void;
}

export const VmClusterSelect = (
  {
    defaultValue,
    fieldName,
    label,
    placeholder,
    required,
    subField,
    tooltip,
    validator,
    subscriptionId,
    location,
    resourceGroupName,
    onChange,
    onError,
    onMissingDependencies,
    disabled,
    testId,
  }: VMClusterProps,
): JSX.Element => {
  const [key, setKey] = React.useState<string>(uniqueGUID());

  const { groupName } = React.useContext(
    FormInputGroupLayoutContext,
  );

  const form: FormState = React.useContext(FormContext);

  const { loading, response, error } = useQueryCall({
    wait: !(subscriptionId && resourceGroupName && location),
    method: apiClients.withRegion(getOciRegion(location)).exaDatabaseApi.listCloudVmClusters,
    options: {
      args: { subscriptionId, resourceGroupName, apiVersion: MultiCloudDatabaseApiVersion },
      caching: ttlOneMinCaching,
      fetchAllPages: true,
      retry: optimizedRetryOption,
    },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: Messages.notifications.failure.messages.loadVMClusters(),
      },
    },
  });

  const items = (response
    && responseItemstoArray<CloudVmClusterSummaryCollection, CloudVmClusterSummary>(response)) || [];

  const options = items
    ?.filter(item => item.lifecycleState !== "TERMINATED" && item.lifecycleState !== "TERMINATING")
    .map(buildVmClusterOption);

  React.useEffect(() => {
    form.setValue(undefined, fieldName, groupName);
    setKey(uniqueGUID());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionId, location, resourceGroupName]);

  React.useEffect(() => {
    setKey(uniqueGUID());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(items)]);

  React.useEffect(() => {
    if (error) {
      onError?.(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const internalOnClick = (): void => {
    const missingDependencies: IdResourceType[] = [];
    if (!subscriptionId) missingDependencies.push(IdResourceType.SUBSCRIPTIONS);
    if (!resourceGroupName) missingDependencies.push(IdResourceType.RESOURCE_GROUPS);
    if (!location) missingDependencies.push(IdResourceType.LOCATION);
    if (missingDependencies.length > 0) {
      onMissingDependencies?.(missingDependencies);
    }
  };

  const derivedPlaceholder = !loading && !error && options.length === 0
    ? Messages.createCommon.loadingNone(label?.toLowerCase() || "")
    : placeholder;

  return (
    <Select
      key={key}
      testId={testId}
      loading={!!resourceGroupName && !!subscriptionId && !!location && loading}
      required={required}
      subField={subField}
      fieldName={fieldName}
      label={label}
      options={options}
      placeholder={derivedPlaceholder}
      defaultValue={defaultValue}
      tooltip={tooltip}
      onClick={internalOnClick}
      onChange={onChange}
      disabled={disabled}
      validator={validator}
    />
  );
};
